import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, Lock, Check, ShoppingBag, Shirt, Scissors, Gamepad2, Music, Stars, Filter, Zap, Eye, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { FullBodyAvatarPreview } from './FullBodyAvatar';

// Category icons and colors
const CATEGORY_CONFIG = {
  hairstyle: { icon: Scissors, color: '#EC4899', label: 'Hairstyles', bgColor: 'from-pink-500/20 to-pink-600/20', slot: 'hairstyle' },
  accessory: { icon: Stars, color: '#FBBF24', label: 'Accessories', bgColor: 'from-amber-500/20 to-amber-600/20', slot: 'accessory' },
  outfit: { icon: Shirt, color: '#8B5CF6', label: 'Outfits', bgColor: 'from-purple-500/20 to-purple-600/20', slot: 'outfit' },
  game_object: { icon: Gamepad2, color: '#22C55E', label: 'Game Forge', bgColor: 'from-green-500/20 to-green-600/20', slot: null },
  beat_sound: { icon: Music, color: '#06B6D4', label: 'Beat Forge', bgColor: 'from-cyan-500/20 to-cyan-600/20', slot: null },
  temple_effect: { icon: Sparkles, color: '#F59E0B', label: 'Temple Effects', bgColor: 'from-orange-500/20 to-orange-600/20', slot: null }
};

// Tier colors
const TIER_CONFIG = {
  common: { color: '#94A3B8', label: 'Common', border: 'border-slate-400/30' },
  uncommon: { color: '#22C55E', label: 'Uncommon', border: 'border-green-500/50' },
  rare: { color: '#A855F7', label: 'Rare', border: 'border-purple-500/50' }
};

// Map store item IDs to avatar values
const STORE_ITEM_TO_AVATAR = {
  // Hairstyles
  hair_galaxy_swirl: { field: 'hair_style', value: 'galaxy_swirl' },
  hair_rainbow_streak: { field: 'hair_style', value: 'rainbow_streak' },
  hair_crystal_crown: { field: 'hair_style', value: 'crystal_crown' },
  hair_flame_tips: { field: 'hair_style', value: 'flame_tips' },
  hair_ocean_waves: { field: 'hair_style', value: 'ocean_waves' },
  // Accessories
  acc_star_crown: { field: 'accessory', value: 'star_crown' },
  acc_magic_goggles: { field: 'accessory', value: 'magic_goggles' },
  acc_butterfly_clips: { field: 'accessory', value: 'butterfly_clips' },
  acc_dragon_horns: { field: 'accessory', value: 'dragon_horns' },
  acc_crystal_earrings: { field: 'accessory', value: 'crystal_earrings' },
  // Outfits
  outfit_starlight_robe: { field: 'outfit', value: 'starlight_robe' },
  outfit_forest_guardian: { field: 'outfit', value: 'forest_guardian' },
  outfit_ocean_explorer: { field: 'outfit', value: 'ocean_explorer' },
};

// Store Item Card Component
const StoreItemCard = ({ item, onTryOn, onPurchase, isSelected }) => {
  const categoryConfig = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.accessory;
  const tierConfig = TIER_CONFIG[item.tier] || TIER_CONFIG.common;
  const CategoryIcon = categoryConfig.icon;
  
  const isOwned = item.is_unlocked;
  const isEquipped = item.is_equipped;
  const canAfford = item.can_afford;
  const canTryOn = ['hairstyle', 'accessory', 'outfit'].includes(item.category);
  
  return (
    <div 
      className={`relative rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
        isSelected
          ? 'border-cyan-400 bg-cyan-500/10 ring-2 ring-cyan-400/50'
          : isEquipped
            ? 'border-purple-500/70 bg-purple-500/10'
            : isOwned 
              ? 'border-green-500/50 bg-green-500/5' 
              : canAfford 
                ? `${tierConfig.border} hover:scale-[1.02] hover:shadow-lg cursor-pointer`
                : 'border-slate-700/30 opacity-60'
      }`}
      onClick={() => canTryOn && onTryOn(item)}
      data-testid={`store-item-${item.id}`}
    >
      {/* Tier Badge */}
      <div 
        className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{ backgroundColor: `${tierConfig.color}20`, color: tierConfig.color }}
      >
        {tierConfig.label}
      </div>
      
      {/* Equipped Badge */}
      {isEquipped && (
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-purple-500 text-white text-xs font-semibold flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Wearing
        </div>
      )}
      
      {/* Owned Badge (not equipped) */}
      {isOwned && !isEquipped && (
        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
      
      <div className="p-4">
        {/* Preview Emoji */}
        <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3 bg-gradient-to-br ${categoryConfig.bgColor}`}>
          <span className="text-4xl">{item.preview_emoji}</span>
        </div>
        
        {/* Item Name */}
        <h3 className="text-white font-semibold text-center mb-1 text-sm">{item.name}</h3>
        
        {/* Category */}
        <div className="flex items-center justify-center gap-1 mb-2">
          <CategoryIcon className="w-3 h-3" style={{ color: categoryConfig.color }} />
          <span className="text-xs" style={{ color: categoryConfig.color }}>{categoryConfig.label}</span>
        </div>
        
        {/* Description */}
        <p className="text-slate-400 text-xs text-center mb-3 line-clamp-2">{item.description}</p>
        
        {/* Action Button */}
        {isEquipped ? (
          <div className="flex items-center justify-center gap-1 py-2 rounded-xl bg-purple-500/20 text-purple-400">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">Equipped</span>
          </div>
        ) : isOwned ? (
          <button
            className="w-full py-2 rounded-xl flex items-center justify-center gap-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
            onClick={(e) => { e.stopPropagation(); onTryOn(item); }}
            data-testid={`equip-${item.id}`}
          >
            <Eye className="w-4 h-4" />
            <span className="text-sm font-semibold">Try On</span>
          </button>
        ) : (
          <button
            disabled={!canAfford}
            className={`w-full py-2 rounded-xl flex items-center justify-center gap-2 transition-all ${
              canAfford 
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:opacity-90' 
                : 'bg-slate-800 text-slate-500'
            }`}
            onClick={(e) => { e.stopPropagation(); onPurchase(item); }}
            data-testid={`buy-${item.id}`}
          >
            {!canAfford && <Lock className="w-4 h-4" />}
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold">{item.cost}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Try On Preview Panel
const TryOnPanel = ({ 
  item, 
  currentAvatar, 
  equippedItems,
  onClose, 
  onEquip, 
  onPurchase,
  isOwned,
  canAfford,
  isPurchasing
}) => {
  if (!item) return null;
  
  const categoryConfig = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.accessory;
  const mapping = STORE_ITEM_TO_AVATAR[item.id];
  
  // Create preview avatar with the item applied
  const previewAvatar = { ...currentAvatar };
  if (mapping) {
    previewAvatar[mapping.field] = mapping.value;
  }
  
  return (
    <div className="bg-slate-800/95 rounded-2xl border border-slate-700/50 p-4 sticky top-24">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Try On Preview</h3>
        <button 
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          data-testid="close-preview"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Avatar Preview */}
      <div className="flex justify-center gap-4 mb-4">
        {/* Current Look */}
        <div className="text-center">
          <div className="bg-slate-900/50 rounded-xl p-3 mb-2">
            <FullBodyAvatarPreview 
              avatar={currentAvatar} 
              size="md"
              isIdle
            />
          </div>
          <p className="text-slate-400 text-xs">Current</p>
        </div>
        
        {/* Arrow */}
        <div className="flex items-center text-slate-500 text-2xl">→</div>
        
        {/* With Item */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-xl p-3 mb-2 ring-2 ring-cyan-400/50">
            <FullBodyAvatarPreview 
              avatar={previewAvatar} 
              size="md"
              isIdle
              showSparkles
            />
          </div>
          <p className="text-cyan-400 text-xs font-medium">With {item.name}</p>
        </div>
      </div>
      
      {/* Item Details */}
      <div className="text-center mb-4">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2 bg-gradient-to-r ${categoryConfig.bgColor}`}>
          <span className="text-xl">{item.preview_emoji}</span>
          <span className="text-white font-semibold">{item.name}</span>
        </div>
        <p className="text-slate-400 text-sm">{item.description}</p>
      </div>
      
      {/* Action Buttons */}
      <div className="space-y-2">
        {isOwned ? (
          <button
            onClick={() => onEquip(item)}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            data-testid="equip-item-btn"
          >
            <CheckCircle className="w-5 h-5" />
            Wear This!
          </button>
        ) : (
          <button
            onClick={() => onPurchase(item)}
            disabled={!canAfford || isPurchasing}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              canAfford 
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90' 
                : 'bg-slate-700 text-slate-500'
            }`}
            data-testid="unlock-item-btn"
          >
            {isPurchasing ? (
              <>Unlocking...</>
            ) : canAfford ? (
              <>
                <Sparkles className="w-5 h-5" />
                Unlock for {item.cost} Sparks
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                Need {item.cost} Sparks
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// "Wear it now?" Modal
const WearItNowModal = ({ item, onWear, onLater, isEquipping }) => {
  if (!item) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onLater}
    >
      <div 
        className="bg-slate-900 rounded-2xl p-6 max-w-sm w-full border border-purple-500/30 animate-bounce-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center">
          {/* Celebration */}
          <div className="text-5xl mb-4 animate-bounce">🎉</div>
          
          <h3 className="text-2xl font-bold text-white mb-2">You unlocked it!</h3>
          <p className="text-slate-400 mb-4">{item.name} is now yours!</p>
          
          {/* Item Preview */}
          <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 ring-2 ring-purple-400/50">
            <span className="text-5xl">{item.preview_emoji}</span>
          </div>
          
          <p className="text-lg text-purple-300 font-medium mb-6">Wear it now?</p>
          
          <div className="flex gap-3">
            <button
              onClick={onLater}
              className="flex-1 py-3 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
              data-testid="wear-later"
            >
              Maybe Later
            </button>
            <button
              onClick={onWear}
              disabled={isEquipping}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="wear-now"
            >
              {isEquipping ? 'Putting on...' : 'Yes, wear it!'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Spark Store Component
const SparkStore = ({ selectedChild, onBack }) => {
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isEquipping, setIsEquipping] = useState(false);
  const [tryOnItem, setTryOnItem] = useState(null);
  const [wearItNowItem, setWearItNowItem] = useState(null);
  
  useEffect(() => {
    if (selectedChild?.id) {
      loadStoreData();
    }
  }, [selectedChild?.id]);
  
  const loadStoreData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/children/${selectedChild.id}/store`);
      setStoreData(response.data);
    } catch (err) {
      console.error('Failed to load store:', err);
      toast.error('Could not load store');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTryOn = (item) => {
    setTryOnItem(item);
  };
  
  const handlePurchase = async (item) => {
    setIsPurchasing(true);
    try {
      const response = await api.post(`/children/${selectedChild.id}/store/purchase`, {
        item_id: item.id
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Show "Wear it now?" modal for equippable items
        if (['hairstyle', 'accessory', 'outfit'].includes(item.category)) {
          setWearItNowItem(item);
        }
        // Refresh store data
        await loadStoreData();
        setTryOnItem(null);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error('Purchase failed:', err);
      toast.error('Purchase failed. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };
  
  const handleEquip = async (item) => {
    setIsEquipping(true);
    try {
      const response = await api.put(`/children/${selectedChild.id}/equip`, {
        item_id: item.id,
        equip: true
      });
      
      if (response.data.success) {
        toast.success(`Now wearing ${item.name}!`);
        await loadStoreData();
        setTryOnItem(null);
        setWearItNowItem(null);
      }
    } catch (err) {
      console.error('Equip failed:', err);
      toast.error('Could not equip item. Please try again.');
    } finally {
      setIsEquipping(false);
    }
  };
  
  // Filter items by category
  const filteredItems = storeData?.items?.filter(item => 
    selectedCategory === 'all' || item.category === selectedCategory
  ) || [];
  
  // Group items by category for display
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});
  
  // Check if selected item is wearable
  const canTryOnSelected = tryOnItem && ['hairstyle', 'accessory', 'outfit'].includes(tryOnItem.category);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full mx-auto mb-4" />
          <p className="text-slate-400">Loading Spark Store...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-900/90 backdrop-blur-lg border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="store-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-purple-400" />
              <h1 className="text-xl sm:text-2xl font-bold text-white">Spark Store</h1>
            </div>
            
            {/* Spark Balance */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-white font-bold">{storeData?.total_sparks || 0}</span>
              <span className="text-slate-400 text-sm hidden sm:inline">sparks</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Welcome Message */}
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Welcome, {selectedChild?.name}!
          </h2>
          <p className="text-slate-400">
            Use your sparks to unlock special items for your avatar and creations!
          </p>
        </div>
        
        {/* Category Filter */}
        <div className="mb-6 overflow-x-auto pb-2">
          <div className="flex gap-2 min-w-max">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                selectedCategory === 'all'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
              data-testid="filter-all"
            >
              <Filter className="w-4 h-4" />
              <span>All Items</span>
            </button>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              const count = storeData?.items?.filter(i => i.category === key).length || 0;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                    selectedCategory === key
                      ? 'text-white border'
                      : 'bg-slate-800/50 text-slate-400 hover:text-white'
                  }`}
                  style={selectedCategory === key ? {
                    backgroundColor: `${config.color}20`,
                    borderColor: `${config.color}50`
                  } : {}}
                  data-testid={`filter-${key}`}
                >
                  <Icon className="w-4 h-4" style={{ color: config.color }} />
                  <span>{config.label}</span>
                  <span className="text-xs opacity-60">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Main Content - Grid + Preview Panel */}
        <div className="flex gap-6">
          {/* Items Grid */}
          <div className={`flex-1 ${canTryOnSelected ? 'max-w-3xl' : ''}`}>
            {selectedCategory === 'all' ? (
              // Grouped by category when showing all
              Object.entries(groupedItems).map(([category, items]) => {
                const config = CATEGORY_CONFIG[category];
                if (!config) return null;
                const Icon = config.icon;
                return (
                  <div key={category} className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon className="w-5 h-5" style={{ color: config.color }} />
                      <h3 className="text-lg font-semibold text-white">{config.label}</h3>
                      <span className="text-slate-500 text-sm">({items.length} items)</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {items.map(item => (
                        <StoreItemCard 
                          key={item.id} 
                          item={item} 
                          onTryOn={handleTryOn}
                          onPurchase={handlePurchase}
                          isSelected={tryOnItem?.id === item.id}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              // Flat grid when filtered
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {filteredItems.map(item => (
                  <StoreItemCard 
                    key={item.id} 
                    item={item} 
                    onTryOn={handleTryOn}
                    onPurchase={handlePurchase}
                    isSelected={tryOnItem?.id === item.id}
                  />
                ))}
              </div>
            )}
            
            {filteredItems.length === 0 && (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No items in this category yet.</p>
              </div>
            )}
          </div>
          
          {/* Try On Preview Panel */}
          {canTryOnSelected && (
            <div className="w-80 flex-shrink-0">
              <TryOnPanel
                item={tryOnItem}
                currentAvatar={storeData?.avatar || {}}
                equippedItems={storeData?.equipped_items || {}}
                onClose={() => setTryOnItem(null)}
                onEquip={handleEquip}
                onPurchase={handlePurchase}
                isOwned={tryOnItem?.is_unlocked}
                canAfford={tryOnItem?.can_afford}
                isPurchasing={isPurchasing}
              />
            </div>
          )}
        </div>
        
        {/* How to earn sparks */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            How to Earn More Sparks
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 rounded-xl bg-slate-800/50">
              <p className="text-purple-400 font-semibold mb-1">Complete Lessons</p>
              <p className="text-slate-400">+5-10 sparks per lesson</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/50">
              <p className="text-green-400 font-semibold mb-1">Create Games</p>
              <p className="text-slate-400">+20 sparks for new games</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/50">
              <p className="text-cyan-400 font-semibold mb-1">Make Beats</p>
              <p className="text-slate-400">+10 sparks per beat</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/50">
              <p className="text-amber-400 font-semibold mb-1">Daily Quests</p>
              <p className="text-slate-400">+10 sparks per quest</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* "Wear it now?" Modal */}
      {wearItNowItem && (
        <WearItNowModal
          item={wearItNowItem}
          onWear={() => handleEquip(wearItNowItem)}
          onLater={() => setWearItNowItem(null)}
          isEquipping={isEquipping}
        />
      )}
    </div>
  );
};

export default SparkStore;
